import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { FaEdit, FaPlay, FaTrash } from 'react-icons/fa';
import LomaTable from 'src/components/molecules/loma-table';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import useRequest from 'src/utils/hooks/useRequest';
import { SearchPrompt } from 'src/api/v1-api';
import { useAuth } from 'src/utils/auth';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import SearchPromptForm from 'src/components/organisms/search-prompt-form';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { CoverLoader } from 'src/components/atoms';

const PAGE_SIZE = 10;
const MAX_SEARCH_PROMPTS = 10;

const SearchPrompts = () => {
  const { marketplace } = useMarketplaceContext();
  const { isAuthenticated } = useAuth();
  const { api } = useRequest();
  const router = useRouter();
  const showToast = useSingleToast();
  const formRef = useRef<HTMLDivElement>(null);
  const isSearchPromptsFeaturedEnabled = useFeatureFlagEnabled('search_prompts');

  const [searchPrompts, setSearchPrompts] = useState<SearchPrompt[]>([]);
  const [selectedSearchPrompt, setSelectedSearchPrompt] = useState<SearchPrompt | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [prevLink, setPrevLink] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<{ key: string; order: string }>({
    key: 'category',
    order: 'desc',
  });

  const columns = [
    {
      label: 'Prompt',
      key: 'prompt',
      width: '70%',
    },
    {
      label: 'Category',
      key: 'category',
      width: '30%',
      render: (searchPrompt: SearchPrompt) => searchPrompt.category?.name,
    },
  ];

  const updateQueryParamAndContextPage = (link?: string | null) => {
    if (link) {
      const url = new URL(link);
      const pageNumber = url.searchParams.get('page') ?? '1';
      const sortByKey = url.searchParams.get('sort_by') ?? 'category';
      const sortOrder = url.searchParams.get('order') ?? 'desc';
      setSortBy({ key: sortByKey, order: sortOrder });
      setPage(parseInt(pageNumber));
    }
  };

  const updateQueryParams = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('page', page.toString());
    currentUrl.searchParams.set('sort_by', sortBy.key);
    currentUrl.searchParams.set('order', sortBy.order);
    window.history.replaceState(
      { ...window.history.state, as: currentUrl.toString(), url: currentUrl.toString() },
      '',
      currentUrl.toString(),
    );
  };

  const handleNextPage = () => {
    if (nextLink) updateQueryParamAndContextPage(nextLink);
  };

  const handlePrevPage = () => {
    if (prevLink) updateQueryParamAndContextPage(prevLink);
  };

  const fetchSearchPrompts = useCallback(async () => {
    try {
      if (!isAuthenticated || !marketplace) return;
      const params = {
        page,
        page_size: PAGE_SIZE,
        sort_by: sortBy.key,
        order: sortBy.order,
      };
      const response = await api.marketplace.searchPrompts.list(marketplace.id, params);
      setSearchPrompts(response.results);
      setNextLink(response.next);
      setPrevLink(response.previous);
      setTotalPages(Math.ceil(response.count / response.page_size));
      setPageNumber(response.page_number);
    } catch (error) {
      showToast({
        status: 'error',
        title: "Couldn't fetch search prompts",
        description: 'Please try again. Contact support if the problem persists.',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace?.id,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, page, PAGE_SIZE, isAuthenticated, marketplace]);

  const updateSortOptions = ({ key, order }: { key: string; order: string }) => {
    setPage(1);
    setSortBy({ key, order });
  };

  useEffect(() => {
    if (page === 0) return;
    updateQueryParams();
    fetchSearchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, page, PAGE_SIZE, isAuthenticated, marketplace]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const parsedPage = parseInt(url.searchParams.get('page') ?? '1');
    const sortByKey = url.searchParams.get('sort_by') ?? 'category';
    const sortOrder = url.searchParams.get('order') ?? 'desc';
    setSortBy({ key: sortByKey, order: sortOrder });
    setPage(parsedPage);
  }, []);

  useEffect(() => {
    if (selectedSearchPrompt && formRef.current) {
      formRef.current.scrollIntoView();
    }
  }, [selectedSearchPrompt, formRef]);

  useEffect(() => {
    if (!isSearchPromptsFeaturedEnabled) {
      router.replace('/admin/queries');
    }
  }, [isSearchPromptsFeaturedEnabled, router]);

  const newSearchPrompt = () => {
    if (searchPrompts.length >= MAX_SEARCH_PROMPTS) {
      showToast({
        status: 'error',
        title: 'Max Search Prompts Reached',
        description: `You can only have ${MAX_SEARCH_PROMPTS} search prompts at a time.`,
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setSelectedSearchPrompt({} as SearchPrompt);
  };

  const editSearchPrompt = (searchPrompt: SearchPrompt) => {
    setSelectedSearchPrompt(searchPrompt);
  };

  const runSearchPrompt = async (searchPrompt: SearchPrompt) => {
    if (!marketplace) return;
    try {
      setIsLoading(true);
      await api.marketplace.searchPrompts.run(marketplace.id, searchPrompt.id);
      showToast({
        status: 'success',
        title: 'Your search is running',
        description:
          'Results can be found in the Listing Approvals section, it may take several minutes to complete.',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      showToast({
        status: 'error',
        title: 'Error running search',
        description: error.message ?? 'Please try again. Contact support if the problem persists.',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace?.id,
          },
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSearchPrompt = async (searchPrompt: SearchPrompt) => {
    if (!marketplace) return;
    try {
      setIsLoading(true);
      await api.marketplace.searchPrompts.delete(marketplace.id, searchPrompt.id);
      setSearchPrompts((prev) => prev.filter((sp) => sp.id !== searchPrompt.id));
      setSelectedSearchPrompt(null);
      showToast({
        status: 'success',
        title: 'Search Prompt Deleted',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      showToast({
        status: 'error',
        title: 'Error deleting Search Prompt',
        description: error.message ?? 'Please try again. Contact support if the problem persists.',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace?.id,
          },
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSearchPrompt = async (searchPrompt: SearchPrompt) => {
    if (!marketplace) return;
    try {
      setIsLoading(true);
      const response = await api.marketplace.searchPrompts.update(
        marketplace.id,
        searchPrompt.id,
        searchPrompt,
      );
      setSearchPrompts((prev) => prev.map((sp) => (sp.id === searchPrompt.id ? response : sp)));
      setSelectedSearchPrompt(null);
      showToast({
        status: 'success',
        title: 'Search Prompt Updated',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      showToast({
        status: 'error',
        title: 'Error updating Search Prompt',
        description: error.message ?? 'Please try again. Contact support if the problem persists.',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace?.id,
          },
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createSearchPrompt = async (searchPrompt: Partial<SearchPrompt>) => {
    if (!marketplace) return;
    try {
      setIsLoading(true);
      const response = await api.marketplace.searchPrompts.create(marketplace.id, searchPrompt);
      setSearchPrompts((prev) => [...prev, response]);
      setSelectedSearchPrompt(null);
      showToast({
        status: 'success',
        title: 'Search Prompt Created',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      showToast({
        status: 'error',
        title: 'Error creating Search Prompt',
        description: error.message ?? 'Please try again. Contact support if the problem persists.',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error,
          level: 'error',
          extras: {
            marketplaceId: marketplace?.id,
          },
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Flex direction="column" width="100%" position="relative">
        {isLoading && <CoverLoader />}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          width="100%"
          gap={2}
          mb={4}
        >
          <Text fontSize="xl" mb={4}>
            Search Prompts
          </Text>
          <Flex direction="row" gap={4} wrap={'wrap'} alignItems={'center'}>
            <Button variant="link" onClick={() => router.push('/admin/queries')}>
              Advanced Mode
            </Button>
            <Button onClick={newSearchPrompt}>Create New</Button>
          </Flex>
        </Flex>
      </Flex>
      <LomaTable
        identifier="id"
        columns={columns}
        data={searchPrompts}
        pageNumber={pageNumber}
        nextLink={nextLink}
        prevLink={prevLink}
        totalPages={totalPages}
        handleNextPage={handleNextPage}
        handlePrevPage={handlePrevPage}
        updateSortOptions={updateSortOptions}
        sortOptions={sortBy}
        actionsMenuItems={[
          {
            label: 'Edit',
            clickHandler: editSearchPrompt,
            icon: <FaEdit />,
          },
          {
            label: 'Run',
            clickHandler: runSearchPrompt,
            icon: <FaPlay />,
          },
          {
            label: 'Delete',
            clickHandler: deleteSearchPrompt,
            icon: <FaTrash />,
          },
        ]}
        usePagination
        sortable
      />
      <div
        ref={formRef}
        style={{ display: selectedSearchPrompt ? 'block' : 'none', width: '100%' }}
      >
        <SearchPromptForm
          searchPrompt={selectedSearchPrompt}
          onUpdate={updateSearchPrompt}
          onCreate={createSearchPrompt}
          onCancel={() => setSelectedSearchPrompt(null)}
        />
      </div>
    </>
  );
};

export default SearchPrompts;
